import { DomainEntity, Id, Json } from '@homeleite/arch';
import { AnimalEvent } from '../animal-event';
import { AnimalSummary } from '../../animal';
import { CalfCondition } from './calf-condition';
import { CalfSize } from './calf-size';
import { ChildBirthFacility } from './childbirth-facility';
import { Gender } from '../../gender';
import { EventType } from '../event-type';
import { ChildbirthType } from './childbirth-type';
import { AuditData } from '../../auditData';

export abstract class Childbirth implements DomainEntity, AnimalEvent {
    protected constructor(
        public readonly id = Id.gen(),
        public date: Date = null,
        public animal: AnimalSummary = new AnimalSummary(),
        public type: string = EventType.CHILDBIRTH.name,
        public created: Date = null,
        public auditData: AuditData = null,
        public milkControlId: string = null
    ) {}

    toJson() {
        return Json.to.simple(this, {
            animal: this.animal.toJson()
        });
    }

    static fromJson(json: any): Childbirth {
        if (json.type == null) {
            throw new Error('Json type must not be null');
        }
        switch (json.type) {
            case ChildbirthType.INDUCTION.name:
                return ChildbirthInduction.fromJson(json);
            case ChildbirthType.ABORTION.name:
                return ChildbirthAbortion.fromJson(json);
            case ChildbirthType.CONVENTIONAL.name:
                return ChildbirthConventional.fromJson(json);
            default:
                throw new Error(`Childbirth [ type: ${json.type} ] not mapped`);
        }
    }
}

export class ChildbirthInduction extends Childbirth {
    constructor(
        public readonly id = Id.gen(),
        public date: Date = null,
        public animal: AnimalSummary = new AnimalSummary(),
        public type: string = ChildbirthType.INDUCTION.name,
        public created: Date = null,
        public auditData: AuditData = null
    ) {
        super(id, date, animal, type, created, auditData);
    }

    toJson() {
        return Json.to.simple(this, {
            date: this.date?.toISOString() ?? null,
            animal: this.animal?.toJson() ?? null,
            type: ChildbirthType.INDUCTION.name
        });
    }

    static fromJson(json: ChildbirthInduction) {
        return Json.from.simple(json, ChildbirthInduction, {
            date: json.date ? new Date(json.date) : null,
            animal: json.animal ? AnimalSummary.fromJson(json.animal) : null,
            type: ChildbirthType.INDUCTION.name,
            created: json.created ? new Date(json.created) : null,
            auditData: json.auditData ? AuditData.fromJson(json.auditData) : null
        });
    }

    static createBy(conventional: ChildbirthInduction, id: string): ChildbirthInduction {
        return new ChildbirthInduction(
            id,
            conventional.date,
            conventional.animal,
            conventional.type
        );
    }
}

export class ChildbirthAbortion extends Childbirth {
    constructor(
        public readonly id = Id.gen(),
        public date: Date = null,
        public animal: AnimalSummary = new AnimalSummary(),
        public type: string = ChildbirthType.ABORTION.name,
        public created: Date = null,
        public auditData: AuditData = null
    ) {
        super(id, date, animal, type, created, auditData);
    }

    toJson() {
        return Json.to.simple(this, {
            date: this.date.toISOString() ?? null,
            animal: this.animal?.toJson() ?? null,
            type: ChildbirthType.ABORTION.name
        });
    }

    static fromJson(json: ChildbirthAbortion) {
        return Json.from.simple(json, ChildbirthAbortion, {
            date: json.date ? new Date(json.date) : null,
            animal: json.animal ? AnimalSummary.fromJson(json.animal) : null,
            type: ChildbirthType.ABORTION.name,
            created: json.created ? new Date(json.created) : null,
            auditData: json.auditData ? AuditData.fromJson(json.auditData) : null
        });
    }
}

export class ChildbirthConventional extends Childbirth {
    constructor(
        public readonly id = Id.gen(),
        public date: Date = null,
        public animal: AnimalSummary = new AnimalSummary(),
        public calfCondition?: CalfCondition,
        public calfSize?: CalfSize,
        public calfWeight = 0,
        public gender: Gender = null,
        public childBirthFacility?: ChildBirthFacility,
        public childBirthOrder = 0,
        public type: string = ChildbirthType.CONVENTIONAL.name,
        public created: Date = null,
        public auditData: AuditData = null
    ) {
        super(id, date, animal, type, created, auditData);
    }

    toJson() {
        return Json.to.simple(this, {
            date: this.date.toISOString() ?? null,
            animal: this.animal?.toJson() ?? null,
            calfCondition: this.calfCondition ?? null,
            calfSize: this.calfSize ?? null,
            calfWeight: this.calfWeight ?? null,
            gender: this.gender.name ?? null,
            childBirthFacility: this.childBirthFacility ?? null,
            type: ChildbirthType.CONVENTIONAL.name
        });
    }

    static fromJson(json: any): ChildbirthConventional {
        return Json.from.simple(json, ChildbirthConventional, {
            date: json.date ? new Date(json.date) : null,
            animal: json.animal ? AnimalSummary.fromJson(json.animal) : null,
            gender: json.gender ? Gender.get(json.gender.name || json.gender) : undefined,
            calfWeight: json.calfWeight,
            childBirthOrder: json.childBirthOrder,
            type: ChildbirthType.CONVENTIONAL.name,
            created: json.created ? new Date(json.created) : null,
            auditData: json.auditData ? AuditData.fromJson(json.auditData) : null
        });
    }

    static createBy(conventional: ChildbirthConventional, id: string): ChildbirthConventional {
        return new ChildbirthConventional(
            id,
            conventional.date,
            conventional.animal,
            conventional.calfCondition,
            conventional.calfSize,
            conventional.calfWeight,
            conventional.gender,
            conventional.childBirthFacility,
            conventional.childBirthOrder,
            conventional.type
        );
    }
}
